import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import OTPInput from '../../components/OTPInput';
import {
  initRecaptcha,
  sendOTP,
  verifyOTP,
  getFirebaseIdToken,
  clearRecaptcha
} from '../../config/firebase';

/**
 * Phone Login Page - OTP-based authentication
 * Handles both login and registration via Firebase Phone Auth
 * UI styled to match original Foodwinc design
 */
const PhoneLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithOTP, logout, user, loading: authLoading, completeProfile } = useAuth();

  // Check if this is the main login page (restaurant/admin only)
  const isMainLoginPage = window.location.pathname === '/login';

  // Get role from URL path or query params (for registration flow)
  const getRoleFromPath = () => {
    const path = window.location.pathname;
    // /login is for restaurant/admin only
    if (path === '/login') return 'restaurant';
    if (path.includes('register-restaurant')) return 'restaurant';
    if (path.includes('register-delivery') || path.includes('become-rider')) return 'delivery';
    // Also check query params for backward compatibility
    const roleParam = searchParams.get('role');
    const validRoles = ['user', 'restaurant', 'delivery'];
    if (validRoles.includes(roleParam)) return roleParam;
    return 'user';
  };
  const role = getRoleFromPath();

  // Check for pending restaurant registration data from static HTML page
  const [pendingRegistration] = useState(() => {
    try {
      const data = sessionStorage.getItem('pendingRestaurantRegistration');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  });

  // State - pre-fill phone from pending registration if available
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState(() => {
    if (pendingRegistration?.phone) {
      // Extract last 10 digits from stored phone
      const digits = pendingRegistration.phone.replace(/\D/g, '').slice(-10);
      return digits;
    }
    return '';
  });
  const [autoTriggerAttempted, setAutoTriggerAttempted] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false); // Prevent auto-redirect during verification

  // Redirect if already logged in (but not during verification)
  useEffect(() => {
    if (!authLoading && user && !isVerifying) {
      navigate('/');
    }
  }, [user, authLoading, navigate, isVerifying]);

  // Initialize reCAPTCHA on mount — ONCE.
  //
  // Previous implementation retried initRecaptcha up to 5 times with
  // increasing delays. Each retry CLEARED and RECREATED the verifier,
  // which registers a new reCAPTCHA instance with Google. Multiple rapid
  // registrations tank the trust score → Google demands visible challenges
  // (image selection) that the user has to solve repeatedly.
  //
  // Fix: wait for the DOM container to appear (poll), then create the
  // verifier EXACTLY ONCE. If creation fails (network error loading the
  // reCAPTCHA script), show an error — don't retry with a fresh verifier.
  useEffect(() => {
    let mounted = true;

    const initializeRecaptcha = async () => {
      // Poll for the DOM element (up to 5s). This replaces the old retry
      // loop that destroyed/recreated the verifier on each attempt.
      for (let i = 0; i < 25 && mounted; i++) {
        if (document.getElementById('recaptcha-container')) break;
        await new Promise(r => setTimeout(r, 200));
      }

      if (!mounted) return;

      // Container should exist now. Create the verifier ONE TIME.
      try {
        const result = initRecaptcha('recaptcha-container', { size: 'invisible' });
        if (result && mounted) {
          setRecaptchaReady(true);
        } else if (mounted) {
          setError('Verification not available. Please refresh the page.');
        }
      } catch (err) {
        console.error('Failed to initialize reCAPTCHA:', err);
        if (mounted) {
          setError('Failed to initialize verification. Please refresh the page.');
        }
      }
    };

    initializeRecaptcha();

    return () => {
      mounted = false;
      clearRecaptcha();
    };
  }, []);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-trigger OTP for pending restaurant registration
  useEffect(() => {
    const shouldAutoTrigger =
      pendingRegistration?.phone &&
      phone.length === 10 &&
      recaptchaReady &&
      !autoTriggerAttempted &&
      step === 'phone' &&
      !loading &&
      role === 'restaurant';

    if (shouldAutoTrigger) {
      setAutoTriggerAttempted(true);
      // Small delay to ensure UI is ready, then trigger OTP
      const timer = setTimeout(async () => {
        try {
          const formattedPhone = `+91${phone}`;
          const result = await sendOTP(formattedPhone);
          setConfirmationResult(result);
          setStep('otp');
          setCountdown(60);
        } catch (err) {
          console.error('Auto-trigger OTP error:', err);
          if (err.code === 'auth/invalid-phone-number') {
            setError('Invalid phone number format');
          } else if (err.code === 'auth/too-many-requests') {
            setError('Too many attempts. Please try again later.');
          } else {
            setError(err.message || 'Failed to send OTP. Please try again.');
          }
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pendingRegistration, phone, recaptchaReady, autoTriggerAttempted, step, loading, role]);

  // Format phone number for display
  const formatPhoneDisplay = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length <= 5) return cleaned;
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  };

  // Handle phone number input
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(value);
    setError('');
  };

  // Send OTP
  const handleSendOTP = useCallback(async (e) => {
    e?.preventDefault();
    setError('');

    if (phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!recaptchaReady) {
      setError('Verification not ready. Please wait...');
      return;
    }

    setLoading(true);

    try {
      const formattedPhone = `+91${phone}`;
      const result = await sendOTP(formattedPhone);
      setConfirmationResult(result);
      setStep('otp');
      setCountdown(60);
    } catch (err) {
      console.error('Send OTP error:', err);
      if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (err.code === 'auth/captcha-check-failed') {
        setError('Verification failed. Please refresh and try again.');
      } else {
        setError(err.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [phone, recaptchaReady]);

  // Verify OTP
  const handleVerifyOTP = useCallback(async (e) => {
    e?.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    setIsVerifying(true); // Prevent auto-redirect while checking restaurant status

    try {
      await verifyOTP(confirmationResult, otp);
      const idToken = await getFirebaseIdToken();

      if (!idToken) {
        throw new Error('Failed to get authentication token');
      }

      // For /login page (restaurant web portal), use loginOnly mode to prevent auto-adding roles
      const loginOptions = isMainLoginPage ? { loginOnly: true } : {};
      const result = await loginWithOTP(idToken, role, loginOptions);

      // For /login page, only allow existing restaurant/admin/support users
      if (isMainLoginPage) {
        const userRole = result.user?.role;
        const userRoles = result.user?.roles || [];

        // If this is a brand new user (just created), they should register first
        if (result.isNewUser) {
          await logout();
          setError('No account found with this number. Please register your restaurant first.');
          setStep('phone');
          setOtp('');
          setConfirmationResult(null);
          setIsVerifying(false);
          return;
        }

        // Check if user has any allowed role (restaurant, admin, or support)
        const allowedRoles = ['restaurant', 'admin', 'support'];
        const hasAllowedRole = allowedRoles.includes(userRole) ||
                               userRoles.some(r => allowedRoles.includes(r));

        if (!hasAllowedRole) {
          await logout();
          setError('This number is not registered as a restaurant. Please register your restaurant first or use the correct login portal.');
          setStep('phone');
          setOtp('');
          setConfirmationResult(null);
          setIsVerifying(false);
          return;
        }

        // For existing restaurant user without a restaurant entity, redirect to onboarding
        if ((userRole === 'restaurant' || userRoles.includes('restaurant')) && !result.hasRestaurant) {
          setIsVerifying(false);
          navigate('/restaurant/onboarding');
          return;
        }
      }

      // If we have pending registration data, complete the profile automatically
      if (pendingRegistration && (result.requiresProfileCompletion || result.isNewUser)) {
        try {
          await completeProfile({
            name: pendingRegistration.ownerName,
            email: pendingRegistration.email
          });
          // Clear the pending registration data
          sessionStorage.removeItem('pendingRestaurantRegistration');
        } catch (profileErr) {
          console.warn('Profile completion failed, will redirect to complete profile:', profileErr);
        }
      }

      setIsVerifying(false);

      // For restaurant role with pending registration, go to onboarding
      // Note: sessionStorage is cleared in RestaurantOnboarding after pre-filling form
      if (role === 'restaurant' && pendingRegistration) {
        navigate('/restaurant/onboarding');
      } else if (result.requiresProfileCompletion) {
        navigate('/auth/complete-profile');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setIsVerifying(false);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP has expired. Please request a new one.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [otp, confirmationResult, loginWithOTP, logout, role, navigate, isMainLoginPage, pendingRegistration, completeProfile]);

  // Resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0) return;

    setOtp('');
    setError('');
    setLoading(true);

    try {
      clearRecaptcha();
      await new Promise(resolve => setTimeout(resolve, 100));
      initRecaptcha('recaptcha-container', { size: 'invisible' });

      const formattedPhone = `+91${phone}`;
      const result = await sendOTP(formattedPhone);
      setConfirmationResult(result);
      setCountdown(60);
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Go back to phone input
  const handleBackToPhone = () => {
    setStep('phone');
    setOtp('');
    setError('');
    setConfirmationResult(null);
  };

  // Check if this is a registration page
  const isRegistrationPage = window.location.pathname.includes('register');

  // Get role-specific content
  const getRoleContent = () => {
    switch (role) {
      case 'restaurant':
        return {
          badge: '🍽️ Restaurant Partner',
          title: isRegistrationPage ? 'Register Your Restaurant' : (isMainLoginPage ? 'Restaurant Portal Login' : 'Restaurant Partner Login'),
          subtitle: isRegistrationPage
            ? 'Join 500+ restaurant partners growing with Foodwinc'
            : (isMainLoginPage ? 'For registered restaurant owners and admins only' : 'Manage your restaurant on Foodwinc'),
          registerLink: isMainLoginPage ? '/register-restaurant' : null,
          features: isRegistrationPage ? [
            { icon: '📈', text: 'Increase orders by up to 30%' },
            { icon: '💰', text: 'No markup - honest pricing' },
            { icon: '📱', text: 'Easy order management app' },
            { icon: '🚀', text: 'Go live within 24 hours' }
          ] : [
            { icon: '📊', text: 'Real-time order management' },
            { icon: '📈', text: 'Analytics & insights dashboard' },
            { icon: '🎯', text: 'Menu & pricing control' }
          ]
        };
      case 'delivery':
        return {
          badge: '🚴 Delivery Partner',
          title: isRegistrationPage ? 'Become a Delivery Partner' : 'Delivery Partner Login',
          subtitle: isRegistrationPage
            ? 'Join our fleet and start earning today'
            : 'Start delivering with Foodwinc',
          registerLink: null,
          features: [
            { icon: '💰', text: 'Earn on your schedule' },
            { icon: '📍', text: 'Smart route optimization' },
            { icon: '⚡', text: 'Instant payment support' }
          ]
        };
      default:
        return {
          badge: null,
          title: 'Welcome back',
          subtitle: 'Sign in to continue ordering delicious food',
          registerLink: '/register-restaurant',
          features: [
            { icon: '🍕', text: 'Order from 500+ restaurants' },
            { icon: '⚡', text: 'Fast & reliable delivery' },
            { icon: '💳', text: 'Secure payment options' }
          ]
        };
    }
  };

  const content = getRoleContent();

  if (authLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.navContainer}>
          <a href="/" style={styles.logo}>Foodwinc</a>
          <div style={styles.navButtons}>
            <a href="/" style={styles.btnOutline}>Home</a>
            {role === 'user' ? (
              <a href="/register-restaurant" style={styles.btnPrimary}>Register Restaurant</a>
            ) : (
              <a href="/login" style={styles.btnPrimary}>Sign In</a>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.mainContainer}>
        <div style={styles.loginWrapper}>
          {/* Left Side - Branding */}
          <div style={styles.brandingSection}>
            <div style={styles.brandingBadge}>
              <span>{role === 'restaurant' ? '🍽️' : role === 'delivery' ? '🚴' : '🍔'}</span>
              {role === 'restaurant' ? 'For Restaurant Owners' :
               role === 'delivery' ? 'For Delivery Partners' :
               'Delicious food, delivered fast'}
            </div>
            <h1 style={styles.brandingTitle}>
              {role === 'restaurant' ? (
                <>Grow Your Restaurant with <span style={styles.titleAccent}>Foodwinc</span></>
              ) : role === 'delivery' ? (
                <>Deliver & Earn with <span style={styles.titleAccent}>Foodwinc</span></>
              ) : (
                <>Your Favorite Food,<br /><span style={styles.titleAccent}>Delivered Fresh</span></>
              )}
            </h1>
            <p style={styles.brandingDescription}>
              {role === 'restaurant' ?
                (isMainLoginPage ?
                  'Access your restaurant dashboard, manage orders, update menus, and track performance. This portal is exclusively for registered restaurant partners and administrators.' :
                  (isRegistrationPage ?
                    'Join 500+ restaurant partners already growing their business with us. Register now and our team will guide you through the onboarding process.' :
                    'Manage your restaurant and track orders on Foodwinc.')) :
               role === 'delivery' ?
                'Flexible hours, competitive earnings, and instant payouts. Join thousands of delivery partners making a great living with Foodwinc.' :
                'Join thousands of food lovers who trust Foodwinc for their daily cravings. Fast delivery, fresh food, and amazing deals await you.'}
            </p>
            <div style={styles.featuresList}>
              {content.features.map((feature, index) => (
                <div key={index} style={styles.featureItem}>
                  <div style={styles.featureIcon}>{feature.icon}</div>
                  <div style={styles.featureText}>{feature.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div style={styles.loginSection}>
            <div style={styles.loginCard}>
              {content.badge && (
                <div style={styles.restaurantBadge}>
                  <span>{content.badge}</span>
                </div>
              )}

              <div style={styles.loginHeader}>
                <h2 style={styles.loginTitle}>{content.title}</h2>
                <p style={styles.loginSubtitle}>{content.subtitle}</p>
              </div>

              {error && (
                <div style={styles.alertError}>
                  {error}
                </div>
              )}

              {step === 'phone' ? (
                <form onSubmit={handleSendOTP}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Phone Number</label>
                    <div style={styles.phoneInputGroup}>
                      <span style={styles.countryCode}>+91</span>
                      <input
                        type="tel"
                        value={formatPhoneDisplay(phone)}
                        onChange={handlePhoneChange}
                        placeholder="98765 43210"
                        style={styles.phoneInput}
                        disabled={loading}
                        autoFocus
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    style={{
                      ...styles.loginBtn,
                      ...(loading || phone.length !== 10 ? styles.loginBtnDisabled : {})
                    }}
                    disabled={loading || phone.length !== 10}
                  >
                    {loading && <span style={styles.spinner}></span>}
                    <span>{loading ? 'Sending OTP...' : 'Get OTP'}</span>
                  </button>

                  <p style={styles.termsText}>
                    By continuing, you agree to our{' '}
                    <a href="/terms" style={styles.termsLink}>Terms of Service</a> and{' '}
                    <a href="/privacy" style={styles.termsLink}>Privacy Policy</a>
                  </p>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP}>
                  <div style={styles.otpHeader}>
                    <p style={styles.otpSubtitle}>
                      Enter the 6-digit code sent to<br />
                      <strong>+91 {formatPhoneDisplay(phone)}</strong>
                      <button type="button" onClick={handleBackToPhone} style={styles.editPhone}>
                        Edit
                      </button>
                    </p>
                  </div>

                  <div style={styles.otpContainer}>
                    <OTPInput
                      value={otp}
                      onChange={setOtp}
                      disabled={loading}
                      error={!!error}
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      ...styles.loginBtn,
                      ...(loading || otp.length !== 6 ? styles.loginBtnDisabled : {})
                    }}
                    disabled={loading || otp.length !== 6}
                  >
                    {loading && <span style={styles.spinner}></span>}
                    <span>{loading ? 'Verifying...' : 'Verify & Continue'}</span>
                  </button>

                  <div style={styles.resendSection}>
                    {countdown > 0 ? (
                      <p style={styles.countdown}>Resend OTP in {countdown}s</p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={loading}
                        style={styles.resendBtn}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </form>
              )}

              {role === 'user' && (
                <div style={styles.signupLink}>
                  Want to partner with us?{' '}
                  <a href="/register-restaurant" style={styles.signupLinkAccent}>
                    Register Restaurant
                  </a>
                </div>
              )}

              {role === 'user' && (
                <div style={styles.signupLink}>
                  Want to deliver?{' '}
                  <a href="/become-rider" style={styles.signupLinkAccent}>
                    Become a Delivery Partner
                  </a>
                </div>
              )}

              {isMainLoginPage && (
                <div style={styles.signupLink}>
                  Don't have a restaurant account?{' '}
                  <a href="/register-restaurant" style={styles.signupLinkAccent}>
                    Register Your Restaurant
                  </a>
                </div>
              )}

              {isMainLoginPage && (
                <div style={styles.signupLink}>
                  Manage multiple outlets?{' '}
                  <a href="/restaurant-manager/login" style={styles.signupLinkAccent}>
                    Restaurant Manager login →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* reCAPTCHA container - invisible */}
      <div id="recaptcha-container"></div>
    </div>
  );
};

// Styles matching original Foodwinc design
const styles = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    lineHeight: 1.6,
    color: '#1a1a2e',
    background: 'linear-gradient(135deg, #fef7f7 0%, #fff5f5 50%, #fef2f2 100%)',
    minHeight: '100vh',
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #fef7f7 0%, #fff5f5 50%, #fef2f2 100%)',
  },
  // Navbar
  navbar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    background: '#FF6961',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(255, 105, 97, 0.3)',
    padding: '16px 0',
  },
  navContainer: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    fontFamily: "'Reddit Sans', sans-serif",
    fontSize: 28,
    fontWeight: 700,
    fontStyle: 'italic',
    color: '#ffffff',
  },
  navButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  btnOutline: {
    padding: '12px 24px',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    background: 'transparent',
    color: '#ffffff',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    transition: 'all 0.3s ease',
  },
  btnPrimary: {
    padding: '12px 24px',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 14,
    textDecoration: 'none',
    background: '#ffffff',
    color: '#FF6961',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
  },
  // Main Container
  mainContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '100px 24px 40px',
  },
  loginWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 80,
    maxWidth: 1100,
    width: '100%',
    flexWrap: 'wrap',
  },
  // Branding Section
  brandingSection: {
    flex: 1,
    maxWidth: 480,
    minWidth: 300,
  },
  brandingBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255, 105, 97, 0.1)',
    padding: '8px 16px',
    borderRadius: 50,
    fontSize: 14,
    fontWeight: 600,
    color: '#FF6961',
    marginBottom: 24,
  },
  brandingTitle: {
    fontSize: 48,
    fontWeight: 800,
    color: '#1a1a2e',
    lineHeight: 1.1,
    marginBottom: 16,
    margin: 0,
  },
  titleAccent: {
    color: '#FF6961',
  },
  brandingTagline: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    marginTop: 16,
  },
  taglineIcon: {
    width: 44,
    height: 44,
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    color: 'white',
    fontWeight: 700,
  },
  taglineContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  taglineText: {
    fontSize: 22,
    fontWeight: 700,
    color: '#1a1a2e',
  },
  taglineAccent: {
    color: '#FF6961',
  },
  taglineSubtext: {
    fontSize: 14,
    color: '#64748b',
  },
  brandingDescription: {
    fontSize: 16,
    color: '#64748b',
    marginBottom: 32,
    lineHeight: 1.7,
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  featureIcon: {
    width: 40,
    height: 40,
    background: 'rgba(255, 105, 97, 0.1)',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  },
  featureText: {
    fontSize: 15,
    color: '#1a1a2e',
    fontWeight: 500,
  },
  // Login Section
  loginSection: {
    flex: 1,
    maxWidth: 440,
    minWidth: 320,
  },
  loginCard: {
    background: 'white',
    borderRadius: 24,
    padding: '48px 40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
  },
  restaurantBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    color: 'white',
    padding: '12px 20px',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 24,
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: 32,
  },
  loginTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: 8,
    margin: '0 0 8px 0',
  },
  loginSubtitle: {
    fontSize: 15,
    color: '#64748b',
    margin: 0,
  },
  // Form
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: '#1a1a2e',
    marginBottom: 8,
  },
  phoneInputGroup: {
    display: 'flex',
    alignItems: 'center',
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  countryCode: {
    padding: '14px 16px',
    background: '#f8fafc',
    color: '#1a1a2e',
    fontWeight: 600,
    fontSize: 15,
    borderRight: '2px solid #e2e8f0',
  },
  phoneInput: {
    flex: 1,
    border: 'none',
    padding: '14px 16px',
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    background: '#f8fafc',
    letterSpacing: 1,
  },
  loginBtn: {
    width: '100%',
    padding: 16,
    background: 'linear-gradient(135deg, #FF6961 0%, #D2625E 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(255, 105, 97, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  loginBtnDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  spinner: {
    width: 20,
    height: 20,
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  alertError: {
    padding: '14px 16px',
    borderRadius: 12,
    marginBottom: 20,
    fontSize: 14,
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
  },
  termsText: {
    fontSize: 12,
    color: '#94a3b8',
    textAlign: 'center',
    marginTop: 20,
  },
  termsLink: {
    color: '#FF6961',
    textDecoration: 'none',
  },
  // OTP Section
  otpHeader: {
    textAlign: 'center',
    marginBottom: 24,
  },
  otpSubtitle: {
    fontSize: 15,
    color: '#64748b',
    lineHeight: 1.6,
    margin: 0,
  },
  editPhone: {
    background: 'none',
    border: 'none',
    color: '#FF6961',
    fontSize: 14,
    cursor: 'pointer',
    marginLeft: 8,
    textDecoration: 'underline',
    fontWeight: 500,
  },
  otpContainer: {
    marginBottom: 24,
  },
  resendSection: {
    textAlign: 'center',
    marginTop: 20,
  },
  countdown: {
    color: '#94a3b8',
    fontSize: 14,
    margin: 0,
  },
  resendBtn: {
    background: 'none',
    border: 'none',
    color: '#FF6961',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  signupLink: {
    textAlign: 'center',
    marginTop: 20,
    fontSize: 14,
    color: '#64748b',
  },
  signupLinkAccent: {
    color: '#FF6961',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

// Add keyframes for spinner animation and Reddit Sans font
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Reddit+Sans:ital,wght@0,400;0,700;1,400;1,700&display=swap');

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @media (max-width: 992px) {
    .login-wrapper {
      flex-direction: column !important;
      gap: 48px !important;
    }
  }
`;
if (!document.querySelector('style[data-phone-login]')) {
  styleSheet.setAttribute('data-phone-login', 'true');
  document.head.appendChild(styleSheet);
}

export default PhoneLogin;
